<template>
  <Fragment>
    <el-form-item label="品牌车系" prop="brandId" class="brand-series-item" >
      <span v-if="disabled">{{ brandSeriesName }}</span>
      <QS-cascader
        v-else
        ref="brandSeriesRef"
        v-model="brandSeries"
        filterable
        clearable
        showAllLevels
        :options="brandList"
        :props="brandCascaderProps"
        :debounce="500"
        :beforeFilter="remoteBrandAndSeries"
        :filterMethod="filterMethod"
        :disabled="disabled"
        @change="brandChange"
        @focus="handleFocus">
      </QS-cascader>
    </el-form-item>
    <el-form-item v-if="showModel" label="车型" prop="modelId" class="model-item">
      <span v-if="disabled">{{ modelName }}</span>
      <QS-select
        v-else
        v-model="selectModel"
        clearable
        filterable
        placeholder="请选择车型"
        :disabled="disabled || modelDisable"
        :loading="modelLoading"
        @change="modelChange"
        :optionData="modelList"
        :optionConfig="{labelKey: 'modelName', valueKey: 'id'}"
        show-hover-tooltip>
      </QS-select>
    </el-form-item>
  </Fragment>
</template>
<script>
import {
  getAllBrand,
  queryBrandAndSeriesByName,
  getSeriesListByBrand,
  getModelsBySeriesId,
} from '@/api/carModel.js'

export default {
  name: 'CarBrandSeriesModel',
  props: ['brandId', 'seriesId', 'modelId', 'showModel', 'brandSeriesModel', 'disabled', 'brandSeriesModelName', 'filterScene'],
  model: {
    prop: 'brandSeriesModel',
    event: 'change',
  },
  data() {
    return {
      suppressingWatchers: false,
      selectBrand: this.brandId,
      selectSeries: this.seriesId,
      selectModel: this.modelId,
      modelList: [], // 车型列表
      modelLoading: false, // 车型加载状态
      brandList: [], // 品牌列表
      brandSeries: [],
      brandCascaderProps: {
        expandTrigger: 'click',
        lazy: true,
        lazyLoad: (node, resolve) => {
          const { value, hasChildren, children } = node
          if (!hasChildren || !value || (Array.isArray(children) && children.length)) {
            resolve()
            return
          }
          const brandItem = this.allBrandList.find((item) => item.value === value)
          getSeriesListByBrand({
            brandId: value,
            modelFilterScene: this.modelFilterScene,
          }).then(({ data }) => {
            const childNodeList = data?.map((item) => {
              return {
                value: item.seriesId,
                label: item.seriesName,
                leaf: true,
              }
            }) || []
            brandItem.children = childNodeList
            resolve(childNodeList)
          }).catch(() => {
            resolve([])
          })
        },
      },
      allBrandList: [],
    }
  },
  watch: {
    // 品牌改变（仅在直接修改 brandId prop 时触发，brandSeriesModel 同步时被抑制）
    brandId(newVal, oldVal) {
      if (this.suppressingWatchers) return
      if (newVal !== oldVal) {
        this.selectBrand = newVal
      }
      this.handleBrandIdClear()
    },
    // 车系改变（仅在直接修改 seriesId prop 时触发，brandSeriesModel 同步时被抑制）
    seriesId(newVal, oldVal) {
      if (this.suppressingWatchers) return
      if (newVal !== oldVal) {
        this.selectSeries = newVal
      }
      this.handleSeriesIdClear()
    },
    // 车型改变（仅在直接修改 modelId prop 时触发）
    modelId(newVal, oldVal) {
      if (this.suppressingWatchers) return
      if (newVal !== oldVal) {
        this.selectModel = newVal
      }
    },
    brandSeriesModel: {
      handler(newVal, oldVal) {
        this.suppressingWatchers = true
        const { brandId, seriesId, modelId } = newVal || {}
        if (seriesId) {
          this.brandSeries = [brandId, seriesId]
        } else {
          this.brandSeries = [brandId]
        }
        const { brandId: oldBrandId, seriesId: oldSeriesId, modelId: oldModelId } = oldVal || {}
        if (brandId !== oldBrandId) {
          this.selectBrand = Number(brandId) || null
          this.handleBrandIdClear()
          if (brandId) {
            this.getSeriesListByBrandId(brandId)
          }
        }
        if (seriesId !== oldSeriesId) {
          this.selectSeries = Number(seriesId) || null
          this.handleSeriesIdClear()
          if (seriesId) {
            this.getCarModelList(seriesId)
          }
        }
        if (modelId !== oldModelId) {
          this.selectModel = modelId ? Number(modelId) : null
        }
        this.$nextTick(() => {
          this.suppressingWatchers = false
        })
      },
      deep: true,
      immediate: true,
    },
  },
  computed: {
    // 车车型禁用标识
    modelDisable() {
      return !this.selectSeries
    },
    brandSeriesName() {
      const { brandSeriesModelName } = this
      const { brandName, seriesName } = brandSeriesModelName || {}
      if (!brandName) return '--'
      if (!seriesName) return brandName
      return `${brandName}/${seriesName}`
    },
    modelName() {
      const { brandSeriesModelName } = this
      return brandSeriesModelName?.modelName || '--'
    },
    // 有些场景不传递prop filterScene，这些场景下filterScene为undefined，接口请求时将参数过滤不传递给服务端
    modelFilterScene() {
      return this.filterScene
    },
  },
  methods: {
    // 获取品牌列表
    async getBrandList() {
      const { code, data } = await getAllBrand({
        modelFilterScene: this.modelFilterScene,
      })
      if (code === 1) {
        const list = data?.map((item) => ({ label: item.brandName, value: item.brandId })) || []
        this.brandList = list
        this.allBrandList = list
      }
    },
    // 根据品牌获取车系
    async getSeriesListByBrandId(brandId) {
      const { data } = await getSeriesListByBrand({
        brandId,
        modelFilterScene: this.modelFilterScene,
      })
      if (this.brandList.length === 0) {
        await this.getBrandList()
      }
      if (data && data.length > 0) {
        const brandItem = this.brandList.find((item) => item.value === brandId)
        brandItem.children = data?.map((item) => {
          return {
            value: item.seriesId,
            label: item.seriesName,
            leaf: true,
          }
        }) || []
        // 某一项品牌更新了子项车系，所以下拉列表值要更新
        this.brandList = [...this.brandList]
      }
    },
    // 获取车型列表
    getCarModelList(val) {
      this.modelLoading = true
      getModelsBySeriesId({
        seriesId: val,
        modelFilterScene: this.modelFilterScene,
      }).then((res) => {
        const { code, data } = res
        if (code === 1) {
          this.modelList = data
        }
      }).finally(() => {
        this.modelLoading = false
      })
    },
    // 清空品牌
    handleBrandIdClear() {
      this.modelList = []
      this.selectSeries = null
      this.selectModel = null
    },
    // 清空车系
    handleSeriesIdClear() {
      this.modelList = []
      this.selectModel = null
    },
    // 重置 父级有用
    // eslint-disable-next-line vue/no-unused-properties
    reset() {
      this.selectBrand = null
      this.selectSeries = null
      this.selectModel = null
      this.$emit('change', { brandId: null, seriesId: null, modelId: null })
    },
    brandChange(newVal) {
      this.handleSeriesIdClear()
      if (!newVal || newVal.length === 0) {
        this.brandList = this.allBrandList
      }
      const brandId = newVal?.[0] || null
      const seriesId = newVal?.[1] || null
      this.selectSeries = Number(seriesId) || null
      this.$emit('update:brandId', brandId)
      this.$emit('update:seriesId', seriesId)
      this.$emit('update:modelId', null)
      this.$emit('change', { brandId, seriesId })
      if (this.showModel && seriesId) {
        this.getCarModelList(seriesId)
      }
    },
    modelChange(newVal) {
      const { selectBrand: brandId, selectSeries: seriesId } = this
      this.$emit('change', {
        brandId,
        seriesId,
        modelId: newVal,
      })
      this.$emit('update:modelId', newVal)
    },
    // 模糊搜索品牌车系
    async remoteBrandAndSeries(val) {
      if (!val || !val.trim()) return false
      const promise = new Promise((resolve, reject) => {
        queryBrandAndSeriesByName({
          brandOrSeriesName: val,
          modelFilterScene: this.modelFilterScene,
        }).then(({ data }) => {
          if (Array.isArray(data) && data.length) {
            this.brandList = data.map((item) => {
              return {
                label: item.brandName,
                value: item.brandId,
                children: item.seriesList?.map((seriesItem) => {
                  return {
                    label: seriesItem.seriesName,
                    value: seriesItem.seriesId,
                    leaf: true,
                  }
                }) || [],
              }
            })
          } else {
            this.brandList = []
          }
          resolve(this.brandList)
        }).catch(() => {
          reject([])
        })
      })
      return promise
    },
    /* eslint-disable-next-line vue/no-unused-properties */
    getLabels() {
      if (this.disabled) {
        return {
          brandSeries: {
            label: '品牌车系',
            key: 'brandId',
            matchValue: this.brandSeriesName,
            hidden: false,
          },
          model: {
            label: '车型',
            key: 'modelId',
            matchValue: this.modelName,
            hidden: false,
          },
        }
      }
      let labels = []
      const node = this.$refs.brandSeriesRef.getCheckedNodes()
      if ((this.brandSeries && this.brandSeries.length > 0) && (node.length === 0 || !node[0])) {
        let list = this.brandList
        this.brandSeries.forEach((value) => {
          const item = list.find((it) => it.value === value)
          if (item) {
            labels.push(item.label)
            list = item.children
          }
        })
      } else if (this.brandSeries && this.brandSeries.length > 0) {
        labels = node[0].pathLabels
      }
      let modelName = ''
      if (this.selectModel) {
        const modelItem = this.modelList.find((item) => item.id === this.selectModel)
        modelName = modelItem.modelName
      }
      return {
        brandSeries: {
          label: '品牌车系',
          key: 'brandId',
          matchValue: labels.join('/') || '全部',
          hidden: false,
        },
        model: {
          label: '车型',
          key: 'modelId',
          matchValue: modelName || '全部',
          hidden: false,
        },
      }
    },
    handleFocus() {
      if (this.brandSeries && this.brandSeries.length > 0) {
        const remoteItem = this.brandList.find((item) => item.value === this.brandSeries[0])
        const children = remoteItem?.children || []
        const allItem = this.allBrandList.find((item) => item.value === this.brandSeries[0])
        if (allItem && children.length > 0) {
          allItem.children = children
        }
      }
      this.brandList = this.allBrandList
    },
    filterMethod() {
      return true
    },
  },
  created() {
    this.getBrandList()
  },
}
</script>
